<template>
  <div class="min-h-screen bg-gray-900">
    <template v-if="isLoading && !categories.length">
      <div class="p-4 max-w-7xl mx-auto">
        <SimpleSpinner />
      </div>
    </template>
    <template v-else>
      <MainLayout :sports="sports">
        <div class="container mx-auto px-4 pb-12">
          <!-- Game Providers Navigation -->
          <div class="py-4 overflow-x-auto">
            <div class="flex space-x-6 min-w-max">
              <button
                v-for="provider in categories"
                :key="provider.id"
                class="flex flex-col items-center group"
                :class="{ 'opacity-100': selectedCategoryId === provider.id, 'opacity-60 hover:opacity-100': selectedCategoryId !== provider.id }"
                @click="selectCategory(provider)"
              >
                <div class="w-4 h-4 mb-0.5">
                  <img :src="provider.image_url" :alt="provider.title" class="w-full h-full object-contain" />
                </div>
                <span class="text-[10px] text-gray-300">{{ provider.title }}</span>
              </button>
            </div>
          </div>

          <!-- Games Grid -->
          <div v-if="gamesLoading" class="flex justify-center py-8">
            <SimpleSpinner />
          </div>
          <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 py-6">
            <div
              v-for="game in games"
              :key="game.id"
              class="group relative rounded-lg overflow-hidden cursor-pointer transition-transform duration-200 hover:scale-105"
              @click="openGame(game)"
            >
              <img
                :src="game.image_url"
                :alt="game.title"
                class="w-full aspect-[4/3] object-cover"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div class="absolute bottom-0 left-0 right-0 p-2">
                  <h3 class="text-sm text-white font-medium truncate">{{ game.title }}</h3>
                </div>
              </div>
              <!-- Loading overlay -->
              <div 
                v-if="gameLoading && selectedGame === game.id" 
                class="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-10"
              >
                <SimpleSpinner class="w-8 h-8" />
                <p class="text-white text-sm mt-2">Loading game...</p>
              </div>
            </div>
          </div>
          
          <!-- No games message -->
          <div v-if="!gamesLoading && games.length === 0" class="py-8 text-center">
            <p class="text-gray-400">No games available for this category</p>
          </div>
          
          <!-- Pagination -->
          <div v-if="pagination.last_page > 1" class="mt-8 py-4 bg-gray-800/50 rounded-lg">
            <div class="flex justify-center items-center space-x-2">
              <!-- Previous page button -->
              <button 
                @click="changePage(pagination.current_page - 1)" 
                :disabled="pagination.current_page === 1"
                class="px-4 py-2 rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <!-- Page numbers -->
              <div class="flex space-x-1">
                <!-- First page -->
                <button 
                  v-if="showFirstPageButton"
                  @click="changePage(1)" 
                  class="px-4 py-2 rounded-md"
                  :class="pagination.current_page === 1 ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'"
                >
                  1
                </button>
                
                <!-- Ellipsis for skipped pages at start -->
                <span v-if="showFirstEllipsis" class="px-3 py-2 text-gray-400">...</span>
                
                <!-- Page numbers around current page -->
                <button 
                  v-for="page in visiblePageNumbers" 
                  :key="page"
                  @click="changePage(page)" 
                  class="px-4 py-2 rounded-md"
                  :class="pagination.current_page === page ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'"
                >
                  {{ page }}
                </button>
                
                <!-- Ellipsis for skipped pages at end -->
                <span v-if="showLastEllipsis" class="px-3 py-2 text-gray-400">...</span>
                
                <!-- Last page -->
                <button 
                  v-if="showLastPageButton"
                  @click="changePage(pagination.last_page)" 
                  class="px-4 py-2 rounded-md"
                  :class="pagination.current_page === pagination.last_page ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'"
                >
                  {{ pagination.last_page }}
                </button>
              </div>
              
              <!-- Next page button -->
              <button 
                @click="changePage(pagination.current_page + 1)" 
                :disabled="pagination.current_page === pagination.last_page"
                class="px-4 py-2 rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import MainLayout from '../layouts/MainLayout.vue'
import SimpleSpinner from '../components/loaders/SimpleSpinner.vue'

// Store initialization
const store = useStore()
const user = computed(() => store.getters.user);

console.log("User in CasinoView:", user.value);

// Cache structure
const cache = ref({
  categories: null,
  games: {}, // Will store games by categoryId_page
  timestamp: {
    categories: null,
    games: {} // Will store timestamps by categoryId_page
  }
})

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000

// Computed sports data from store
const sports = computed(() => store.state.sports || [])

// State for API data
const categories = ref([])
const games = ref([])
const selectedCategoryId = ref('all')
const isLoading = ref(true)
const gamesLoading = ref(false)
const error = ref(null)

// Game loading state
const gameLoading = ref(false)
const selectedGame = ref(null)

// Pagination state
const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 20,
  total: 0
})

// Get auth token from localStorage
const getAuthToken = () => {
  const authData = JSON.parse(localStorage.getItem("forbet_auth") || "{}")
  return authData.accessToken
}

// Check if cache is valid
const isCacheValid = (type, key = null) => {
  const now = Date.now()
  if (type === 'categories') {
    return cache.value.timestamp.categories && 
           (now - cache.value.timestamp.categories) < CACHE_DURATION
  }
  if (type === 'games' && key) {
    return cache.value.timestamp.games[key] && 
           (now - cache.value.timestamp.games[key]) < CACHE_DURATION
  }
  return false
}

// Cache data
const setCacheData = (type, data, key = null) => {
  if (type === 'categories') {
    cache.value.categories = data
    cache.value.timestamp.categories = Date.now()
  }
  if (type === 'games' && key) {
    cache.value.games[key] = data
    cache.value.timestamp.games[key] = Date.now()
  }
}

// Get cached data
const getCachedData = (type, key = null) => {
  if (type === 'categories') {
    return cache.value.categories
  }
  if (type === 'games' && key) {
    return cache.value.games[key]
  }
  return null
}

// Fetch categories from API
const fetchCategories = async (useCache = true) => {
  // Check cache first if allowed
  if (useCache && isCacheValid('categories')) {
    categories.value = getCachedData('categories')
    isLoading.value = false
    
    // Fetch fresh data in the background
    fetchCategories(false).catch(console.error)
    return
  }

  if (!useCache) {
    isLoading.value = true
  }
  error.value = null
  
  try {
    const token = getAuthToken()
    const response = await axios.get(
      'https://api.newbet365.org/api/slots/categories',
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    
    if (response.data.status === 'success') {
      const newCategories = response.data.data
      categories.value = newCategories
      setCacheData('categories', newCategories)

      // Only fetch games if this is the initial load
      if (useCache && categories.value.length > 0) {
        const allCategory = categories.value.find(cat => cat.id === 'all') || categories.value[0]
        selectedCategoryId.value = allCategory.id
        await fetchGames(allCategory.id, 1)
      }
    } else {
      throw new Error('Failed to fetch categories')
    }
  } catch (err) {
    console.error('Error fetching categories:', err)
    error.value = 'Failed to load game categories. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

// Fetch games for a specific category and page
const fetchGames = async (categoryId, page = 1, useCache = true) => {
  const cacheKey = `${categoryId}_${page}`

  // Check cache first if allowed
  if (useCache && isCacheValid('games', cacheKey)) {
    const cachedData = getCachedData('games', cacheKey)
    games.value = cachedData.games
    pagination.value = cachedData.pagination
    gamesLoading.value = false

    // Fetch fresh data in the background
    fetchGames(categoryId, page, false).catch(console.error)
    return
  }

  if (!useCache) {
    gamesLoading.value = true
  }
  error.value = null
  
  try {
    const token = getAuthToken()
    const response = await axios.get(
      `https://api.newbet365.org/api/slots/games/${categoryId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          page: page
        }
      }
    )
    
    if (response.data.status === 'success') {
      const newGames = response.data.data.games || []
      const newPagination = response.data.data.pagination || {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      }

      games.value = newGames
      pagination.value = newPagination

      // Cache the data
      setCacheData('games', {
        games: newGames,
        pagination: newPagination
      }, cacheKey)
    } else {
      throw new Error(response.data.message || 'Failed to fetch games')
    }
  } catch (err) {
    console.error('Error fetching games:', err)
    error.value = err.response?.data?.message || 'Failed to load games. Please try again later.'
    if (!useCache) {
      games.value = []
      pagination.value = {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      }
    }
  } finally {
    gamesLoading.value = false
  }
}

// Function to open the game
const openGame = async (game) => {
  console.log('Opening game:', game)
  
  if (!game.original_id) {
    console.error('Game has no original_id')
    return
  }
  

  console.log("User in openGame:", user)
  if (!user.value) {
    alert('You need to be logged in to play games')
    return
  }
  
  selectedGame.value = game.id
  gameLoading.value = true
  
  try {
    const token = getAuthToken()
    const response = await axios.post(
      'https://api.newbet365.org/api/slots/open-game',
      {
        gameId: game.original_id
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    
    if (response.data.status === 'success' && response.data.data.url) {
      // Get the game URL from the response - it already contains the token
      const gameUrl = response.data.data.url
      
      // Open the game URL in a new window/tab
      window.open(gameUrl, '_blank')
    } else {
      throw new Error('Failed to get game URL')
    }
  } catch (err) {
    console.error('Error opening game:', err)
    alert('Failed to open game. Please try again later.')
  } finally {
    gameLoading.value = false
    selectedGame.value = null
  }
}

// Handle category selection
const selectCategory = async (category) => {
  selectedCategoryId.value = category.id
  await fetchGames(category.id, 1)
}

// Handle page change
const changePage = async (page) => {
  if (page < 1 || page > pagination.value.last_page) return
  
  window.scrollTo({ top: 0, behavior: 'smooth' })
  await fetchGames(selectedCategoryId.value, page)
}

// Computed properties for pagination UI
const visiblePageNumbers = computed(() => {
  const current = pagination.value.current_page
  const last = pagination.value.last_page
  const delta = 2
  
  let range = []
  range.push(current)
  
  for (let i = 1; i <= delta; i++) {
    if (current - i > 1) {
      range.unshift(current - i)
    }
  }
  
  for (let i = 1; i <= delta; i++) {
    if (current + i < last) {
      range.push(current + i)
    }
  }
  
  return range
})

const showFirstPageButton = computed(() => {
  return !visiblePageNumbers.value.includes(1)
})

const showLastPageButton = computed(() => {
  return !visiblePageNumbers.value.includes(pagination.value.last_page) && pagination.value.last_page > 1
})

const showFirstEllipsis = computed(() => {
  return visiblePageNumbers.value.length > 0 && visiblePageNumbers.value[0] > 2
})

const showLastEllipsis = computed(() => {
  return visiblePageNumbers.value.length > 0 && 
         visiblePageNumbers.value[visiblePageNumbers.value.length - 1] < pagination.value.last_page - 1
})

// Clear cache when component is unmounted
onUnmounted(() => {
  cache.value = {
    categories: null,
    games: {},
    timestamp: {
      categories: null,
      games: {}
    }
  }
})

// Fetch data on component mount
onMounted(() => {
  fetchCategories()
})
</script>